import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, TrendingUp, TrendingDown, Target, CheckCircle2, AlertCircle } from "lucide-react";

const GoalsView = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [deltaInfo, setDeltaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("year");

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [userRes, deltaRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/agent/goal-delta/${userId}`)
      ]);
      setUser(userRes.data);
      setDeltaInfo(deltaRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load goals");
      setLoading(false);
    }
  };

  if (loading || !user || !deltaInfo) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#3dd9c5]"></div>
      </div>
    );
  }

  const progressPercentage = deltaInfo.progressPercentage;
  const currentRevenue = user.currentRevenue;
  const revenueGoal = user.revenueGoal;
  const remaining = revenueGoal - currentRevenue;

  const getPacingStatus = () => {
    if (progressPercentage >= 90) return { status: "Ahead", icon: TrendingUp, color: "text-green-400" };
    if (progressPercentage >= 60) return { status: "On Pace", icon: Target, color: "text-[#3dd9c5]" };
    return { status: "Behind", icon: TrendingDown, color: "text-red-400" };
  };

  const pacing = getPacingStatus();
  const PacingIcon = pacing.icon;

  const milestones = [
    { value: revenueGoal * 0.25, label: "25% Milestone", achieved: currentRevenue >= revenueGoal * 0.25 },
    { value: revenueGoal * 0.5, label: "50% Milestone", achieved: currentRevenue >= revenueGoal * 0.5 },
    { value: revenueGoal * 0.75, label: "75% Milestone", achieved: currentRevenue >= revenueGoal * 0.75 },
    { value: revenueGoal, label: "100% Goal", achieved: currentRevenue >= revenueGoal }
  ];

  const activityMetrics = [
    { label: "Accounts Engaged", value: 12, peer: 15, status: "below" },
    { label: "Deals Created", value: 8, peer: 10, status: "below" },
    { label: "Emails Sent", value: 145, peer: 120, status: "above" },
    { label: "Meetings Held", value: 18, peer: 20, status: "below" }
  ];

  return (
    <div className="space-y-6" data-testid="goals-view">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Trophy className="h-8 w-8 text-[#3dd9c5]" />
          Goals & Progress
        </h1>
        <p className="text-gray-400">Track your performance and milestones</p>
      </div>

      {/* Time Period Selector */}
      <div className="flex gap-2">
        {["month", "quarter", "year"].map((period) => (
          <Button
            key={period}
            onClick={() => setTimePeriod(period)}
            variant={timePeriod === period ? "default" : "outline"}
            className={timePeriod === period
              ? "bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
              : "border-gray-700 text-gray-400 hover:bg-white/5"
            }
            data-testid={`period-${period}`}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)}
          </Button>
        ))}
      </div>

      {/* Main Progress Card */}
      <Card className="bg-[#0f1419] border-gray-800/50 p-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h2 className="text-4xl font-bold text-white mb-2">
              ${(revenueGoal / 1000000).toFixed(1)}M
            </h2>
            <p className="text-gray-400">Annual Revenue Goal</p>
          </div>
          <Badge className={`${pacing.color} border-0 bg-white/10 text-lg px-4 py-2 flex items-center gap-2`}>
            <PacingIcon className="h-5 w-5" />
            {pacing.status}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Current Progress</span>
            <span className="text-lg font-bold text-white">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-6 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#3dd9c5] to-[#2dd4bf] transition-all duration-500 flex items-center justify-end px-2"
              style={{ width: `${Math.min(100, progressPercentage)}%` }}
            >
              {progressPercentage > 10 && (
                <span className="text-xs font-bold text-white">
                  ${(currentRevenue / 1000).toFixed(0)}K
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">Current Revenue</p>
            <p className="text-2xl font-bold text-white">${(currentRevenue / 1000).toFixed(0)}K</p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">Remaining to Goal</p>
            <p className="text-2xl font-bold text-[#3dd9c5]">${(remaining / 1000).toFixed(0)}K</p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">Pipeline Value</p>
            <p className="text-2xl font-bold text-yellow-400">$343K</p>
          </div>
        </div>
      </Card>

      {/* Milestones */}
      <Card className="bg-[#0f1419] border-gray-800/50 p-6">
        <h3 className="text-xl font-semibold text-white mb-6">Milestones</h3>
        <div className="space-y-4">
          {milestones.map((milestone, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                milestone.achieved
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-white/5 border-gray-700/50"
              }`}
              data-testid={`milestone-${idx}`}
            >
              <div className="flex items-center gap-3">
                {milestone.achieved ? (
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-gray-500" />
                )}
                <div>
                  <p className="font-semibold text-white">{milestone.label}</p>
                  <p className="text-sm text-gray-400">${(milestone.value / 1000).toFixed(0)}K</p>
                </div>
              </div>
              {milestone.achieved && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                  Achieved
                </Badge>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Activity Metrics vs Peers */}
      <Card className="bg-[#0f1419] border-gray-800/50 p-6">
        <h3 className="text-xl font-semibold text-white mb-6">Activity Metrics vs Peers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activityMetrics.map((metric, idx) => (
            <div
              key={idx}
              className="p-4 bg-white/5 border border-gray-700/50 rounded-lg"
              data-testid={`metric-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">{metric.label}</p>
                <Badge className={`${
                  metric.status === "above"
                    ? "bg-green-500/20 text-green-400 border-green-500/50"
                    : "bg-red-500/20 text-red-400 border-red-500/50"
                } text-xs`}>
                  {metric.status === "above" ? "Above" : "Below"} Avg
                </Badge>
              </div>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-xs text-gray-500">You</p>
                  <p className="text-2xl font-bold text-white">{metric.value}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Peer Avg</p>
                  <p className="text-xl font-semibold text-gray-400">{metric.peer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recommended Actions */}
      {pacing.status === "Behind" && (
        <Card className="bg-[#0f1419] border-red-500/30 p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-400" />
            Recommended Actions to Get Back on Pace
          </h3>
          <div className="space-y-3">
            {deltaInfo.recommendedActions.map((action, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="p-1 bg-red-500/20 rounded mt-0.5">
                  <Target className="h-4 w-4 text-red-400" />
                </div>
                <p className="text-sm text-gray-300 flex-1">{action}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default GoalsView;
